/**
 * Art media (spec §9.1).
 *
 * A single round pen is what a prototype draws with. A child can tell the
 * difference between a marker and a crayon instantly, and the difference is
 * most of why making a picture feels good, so each medium is modelled here:
 * how it lays down, how pressure reads, how it grains, how it sparkles.
 *
 * Two rules govern everything in this file.
 *
 *  1. DETERMINISM. Grain, speckle and spray are random-looking but never
 *     actually random. Every stroke carries a seed, and every medium draws
 *     from a seeded generator, so undo → redo → reload reproduces the child's
 *     picture mark for mark. A picture that reshuffles itself when you undo is
 *     not the child's picture any more.
 *
 *  2. STAMP SPACING, NOT EVENT SPACING. Textured media are drawn as evenly
 *     spaced stamps along a resampled path, so a fast stroke and a slow stroke
 *     lay down the same amount of colour. Sampling straight from pointer
 *     events makes a slow hand look heavy and a quick one look starved.
 */

import type { StrokePoint } from './engine';

export type BrushKind =
  | 'marker'
  | 'pencil'
  | 'paint'
  | 'watercolor'
  | 'crayon'
  | 'glitter'
  | 'spray'
  | 'rainbow';

export interface Medium {
  kind: BrushKind;
  name: string;
  /** One short line a parent or child can understand. */
  hint: string;
  /** Barrel colour for the drawn implement in the tray. */
  barrel: string;
  /** True when the medium supplies its own colour and ignores the palette. */
  ownColor?: boolean;
  /** Multiplier applied to the chosen brush size. */
  scale: number;
}

export const MEDIA: Medium[] = [
  { kind: 'marker', name: 'Marker', hint: 'Bright and bold', barrel: '#E23359', scale: 1 },
  { kind: 'pencil', name: 'Pencil', hint: 'Light and scratchy', barrel: '#FFC94D', scale: 0.75 },
  { kind: 'paint', name: 'Paint', hint: 'Brushy with bristles', barrel: '#2E7A4E', scale: 1.15 },
  { kind: 'watercolor', name: 'Water', hint: 'Soft and see-through', barrel: '#20B2BE', scale: 1.5 },
  { kind: 'crayon', name: 'Crayon', hint: 'Waxy and rough', barrel: '#F2803C', scale: 1.1 },
  { kind: 'glitter', name: 'Glitter', hint: 'Sparkly', barrel: '#7C4FE8', scale: 1.1 },
  { kind: 'spray', name: 'Spray', hint: 'Misty dots', barrel: '#4A7BE0', scale: 1.6 },
  { kind: 'rainbow', name: 'Rainbow', hint: 'Every colour at once', barrel: '#E233AD', ownColor: true, scale: 1 },
];

export function medium(kind: BrushKind): Medium {
  return MEDIA.find((m) => m.kind === kind) ?? MEDIA[0];
}

/* ------------------------------------------------------------------ *
 * Seeded randomness
 * ------------------------------------------------------------------ */

/** mulberry32 — small, fast, and identical on every device and every replay. */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Stable 32-bit hash so a region id always seeds the same texture. */
export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/* ------------------------------------------------------------------ *
 * Path resampling
 * ------------------------------------------------------------------ */

export interface Sample extends StrokePoint {
  /** Distance along the stroke, in art units. */
  d: number;
  /** Unit direction at this sample. */
  nx: number;
  ny: number;
}

/** Even samples along the polyline, with direction and cumulative distance. */
export function resample(points: StrokePoint[], spacing: number): Sample[] {
  const step = Math.max(0.6, spacing);
  if (points.length === 0) return [];
  if (points.length === 1) {
    return [{ ...points[0], d: 0, nx: 1, ny: 0 }];
  }
  const out: Sample[] = [];
  let carry = 0;
  let travelled = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) continue;
    const nx = dx / len;
    const ny = dy / len;
    let t = carry;
    while (t <= len) {
      const k = t / len;
      out.push({
        x: a.x + dx * k,
        y: a.y + dy * k,
        p: a.p + (b.p - a.p) * k,
        d: travelled + t,
        nx,
        ny,
      });
      t += step;
    }
    carry = t - len;
    travelled += len;
  }
  if (out.length === 0) out.push({ ...points[0], d: 0, nx: 1, ny: 0 });
  return out;
}

/** Total length of a stroke in art units. */
export function pathLength(points: StrokePoint[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
  }
  return total;
}

/* ------------------------------------------------------------------ *
 * Colour helpers
 * ------------------------------------------------------------------ */

export function rgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgba(hex: string, alpha: number): string {
  const [r, g, b] = rgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function mix(hex: string, towards: string, amount: number): string {
  const [r1, g1, b1] = rgb(hex);
  const [r2, g2, b2] = rgb(towards);
  const f = (a: number, b: number) => Math.round(a + (b - a) * amount);
  return `rgb(${f(r1, r2)},${f(g1, g2)},${f(b1, b2)})`;
}

/* ------------------------------------------------------------------ *
 * The media themselves
 * ------------------------------------------------------------------ */

export interface StrokeStyle {
  color: string;
  size: number;
  kind: BrushKind;
  seed: number;
}

/**
 * Lays a stroke down onto a context in art coordinates.
 *
 * `progress` (0..1) draws only the first part of the stroke, which is how the
 * lift-off animations replay a mark without touching the saved document.
 */
export function paintMedium(
  ctx: CanvasRenderingContext2D,
  points: StrokePoint[],
  style: StrokeStyle,
  progress = 1,
) {
  const { kind, size, seed } = style;
  const spacing = STAMP_SPACING[kind] * size;
  const all = resample(points, spacing);
  const samples = progress >= 1 ? all : all.slice(0, Math.max(1, Math.ceil(all.length * progress)));
  if (samples.length === 0) return;
  const total = Math.max(1, all[all.length - 1].d);
  const rand = rng(seed);

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  switch (kind) {
    case 'marker':
      drawMarker(ctx, samples, style);
      break;
    case 'pencil':
      drawPencil(ctx, samples, style, rand);
      break;
    case 'paint':
      drawPaint(ctx, samples, style, rand);
      break;
    case 'watercolor':
      drawWatercolor(ctx, samples, style, rand);
      break;
    case 'crayon':
      drawCrayon(ctx, samples, style, rand);
      break;
    case 'glitter':
      drawGlitter(ctx, samples, style, rand);
      break;
    case 'spray':
      drawSpray(ctx, samples, style, rand);
      break;
    case 'rainbow':
      drawRainbow(ctx, samples, style, total);
      break;
  }

  ctx.restore();
}

/** Stamp spacing as a fraction of brush size, per medium. */
const STAMP_SPACING: Record<BrushKind, number> = {
  marker: 0.5,
  pencil: 0.16,
  paint: 0.18,
  watercolor: 0.22,
  crayon: 0.14,
  glitter: 0.2,
  spray: 0.3,
  rainbow: 0.3,
};

function widthAt(s: Sample, size: number, floor = 0.55) {
  return size * (floor + s.p * (1 - floor));
}

function polyline(ctx: CanvasRenderingContext2D, samples: Sample[]) {
  ctx.beginPath();
  ctx.moveTo(samples[0].x, samples[0].y);
  for (let i = 1; i < samples.length; i++) {
    const a = samples[i - 1];
    const b = samples[i];
    ctx.quadraticCurveTo(a.x, a.y, (a.x + b.x) / 2, (a.y + b.y) / 2);
  }
  const last = samples[samples.length - 1];
  ctx.lineTo(last.x, last.y);
}

/** Marker: opaque, even, with the faintest bleed at the edge. */
function drawMarker(ctx: CanvasRenderingContext2D, samples: Sample[], s: StrokeStyle) {
  ctx.strokeStyle = rgba(s.color, 0.14);
  ctx.lineWidth = s.size * 1.2;
  polyline(ctx, samples);
  ctx.stroke();

  ctx.strokeStyle = rgba(s.color, 0.95);
  for (let i = 1; i < samples.length; i++) {
    const a = samples[i - 1];
    const b = samples[i];
    ctx.lineWidth = widthAt(b, s.size, 0.7);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
}

/** Pencil: a light core plus graphite grain that catches the paper. */
function drawPencil(
  ctx: CanvasRenderingContext2D,
  samples: Sample[],
  s: StrokeStyle,
  rand: () => number,
) {
  ctx.strokeStyle = rgba(s.color, 0.3);
  ctx.lineWidth = s.size * 0.42;
  polyline(ctx, samples);
  ctx.stroke();

  ctx.fillStyle = rgba(s.color, 0.5);
  for (const sm of samples) {
    const spread = s.size * 0.42 * (0.6 + sm.p * 0.6);
    const grains = 3;
    for (let g = 0; g < grains; g++) {
      const off = (rand() - 0.5) * spread * 2;
      const along = (rand() - 0.5) * s.size * 0.2;
      const px = sm.x + -sm.ny * off + sm.nx * along;
      const py = sm.y + sm.nx * off + sm.ny * along;
      const r = s.size * (0.035 + rand() * 0.06);
      ctx.globalAlpha = 0.25 + rand() * 0.4;
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

/** Paint: separate bristles, so the stroke shows the brush that made it. */
function drawPaint(
  ctx: CanvasRenderingContext2D,
  samples: Sample[],
  s: StrokeStyle,
  rand: () => number,
) {
  const bristles = 9;
  const offsets: number[] = [];
  const shades: string[] = [];
  for (let i = 0; i < bristles; i++) {
    const t = i / (bristles - 1) - 0.5;
    offsets.push(t + (rand() - 0.5) * 0.08);
    // Slight tonal variation reads as loaded and unloaded bristles.
    shades.push(mix(s.color, i % 2 === 0 ? '#FFFFFF' : '#000000', 0.04 + rand() * 0.1));
  }

  for (let b = 0; b < bristles; b++) {
    ctx.strokeStyle = shades[b];
    ctx.globalAlpha = 0.55 + (b % 3) * 0.12;
    ctx.lineWidth = s.size * 0.3;
    ctx.beginPath();
    for (let i = 0; i < samples.length; i++) {
      const sm = samples[i];
      const spread = s.size * (0.42 + sm.p * 0.28);
      const px = sm.x + -sm.ny * offsets[b] * spread * 2;
      const py = sm.y + sm.nx * offsets[b] * spread * 2;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

/** Watercolour: translucent pools that deepen where they overlap. */
function drawWatercolor(
  ctx: CanvasRenderingContext2D,
  samples: Sample[],
  s: StrokeStyle,
  rand: () => number,
) {
  for (const sm of samples) {
    const r = s.size * (0.42 + sm.p * 0.3) * (0.85 + rand() * 0.35);
    const cx = sm.x + (rand() - 0.5) * s.size * 0.16;
    const cy = sm.y + (rand() - 0.5) * s.size * 0.16;
    const grad = ctx.createRadialGradient(cx, cy, r * 0.1, cx, cy, r);
    grad.addColorStop(0, rgba(s.color, 0.13));
    grad.addColorStop(0.7, rgba(s.color, 0.09));
    grad.addColorStop(1, rgba(s.color, 0));
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Crayon: waxy, uneven coverage that skips over the tooth of the paper. */
function drawCrayon(
  ctx: CanvasRenderingContext2D,
  samples: Sample[],
  s: StrokeStyle,
  rand: () => number,
) {
  ctx.strokeStyle = rgba(s.color, 0.42);
  ctx.lineWidth = s.size * 0.8;
  polyline(ctx, samples);
  ctx.stroke();

  ctx.fillStyle = s.color;
  for (const sm of samples) {
    const spread = s.size * 0.36 * (0.75 + sm.p * 0.4);
    for (let g = 0; g < 5; g++) {
      if (rand() < 0.14) continue; // skipped patch — the tooth of the paper
      const off = (rand() - 0.5) * spread * 2;
      const px = sm.x + -sm.ny * off;
      const py = sm.y + sm.nx * off;
      ctx.globalAlpha = 0.32 + rand() * 0.45;
      ctx.beginPath();
      ctx.ellipse(
        px,
        py,
        s.size * (0.17 + rand() * 0.14),
        s.size * (0.12 + rand() * 0.1),
        Math.atan2(sm.ny, sm.nx),
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

/** Glitter: a soft base with bright flecks sitting on top of it. */
function drawGlitter(
  ctx: CanvasRenderingContext2D,
  samples: Sample[],
  s: StrokeStyle,
  rand: () => number,
) {
  ctx.strokeStyle = rgba(s.color, 0.62);
  ctx.lineWidth = s.size * 0.82;
  polyline(ctx, samples);
  ctx.stroke();

  const bright = mix(s.color, '#FFFFFF', 0.75);
  const deep = mix(s.color, '#000000', 0.25);
  for (const sm of samples) {
    const spread = s.size * 0.46;
    for (let g = 0; g < 3; g++) {
      const off = (rand() - 0.5) * spread * 2;
      const along = (rand() - 0.5) * s.size * 0.3;
      const px = sm.x + -sm.ny * off + sm.nx * along;
      const py = sm.y + sm.nx * off + sm.ny * along;
      const r = s.size * (0.03 + rand() * 0.06);
      // A fleck has to catch the light against dark paint AND against bare
      // paper, so some are lighter than the stroke and some are darker.
      const roll = rand();
      ctx.fillStyle = roll > 0.62 ? '#FFFFFF' : roll > 0.3 ? bright : deep;
      ctx.globalAlpha = 0.45 + rand() * 0.55;
      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

/** Spray: airbrush mist, denser in the middle. */
function drawSpray(
  ctx: CanvasRenderingContext2D,
  samples: Sample[],
  s: StrokeStyle,
  rand: () => number,
) {
  ctx.fillStyle = s.color;
  for (const sm of samples) {
    const radius = s.size * 0.5 * (0.75 + sm.p * 0.5);
    const dots = 26;
    for (let d = 0; d < dots; d++) {
      // sqrt keeps the mist from bunching at the centre.
      const rr = Math.sqrt(rand()) * radius;
      const ang = rand() * Math.PI * 2;
      const px = sm.x + Math.cos(ang) * rr;
      const py = sm.y + Math.sin(ang) * rr;
      ctx.globalAlpha = 0.1 + (1 - rr / radius) * 0.22;
      ctx.beginPath();
      ctx.arc(px, py, s.size * 0.022 + rand() * s.size * 0.018, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

/** Rainbow: hue travels with the hand, so the stroke records the movement. */
function drawRainbow(
  ctx: CanvasRenderingContext2D,
  samples: Sample[],
  s: StrokeStyle,
  total: number,
) {
  for (let i = 1; i < samples.length; i++) {
    const a = samples[i - 1];
    const b = samples[i];
    const hue = ((b.d / Math.max(total, 1)) * 300 + (s.seed % 360)) % 360;
    ctx.strokeStyle = `hsl(${hue} 88% 58%)`;
    ctx.lineWidth = widthAt(b, s.size, 0.7);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
}

/* ------------------------------------------------------------------ *
 * Fill finishes
 * ------------------------------------------------------------------ */

export type Finish = 'solid' | 'glitter' | 'waves' | 'dots' | 'stars' | 'stripes';

export const FINISHES: Array<{ id: Finish; name: string }> = [
  { id: 'solid', name: 'Plain colour' },
  { id: 'glitter', name: 'Glitter' },
  { id: 'waves', name: 'Waves' },
  { id: 'dots', name: 'Spots' },
  { id: 'stars', name: 'Stars' },
  { id: 'stripes', name: 'Stripes' },
];

/**
 * A fill is stored as a paint spec string so `regionColors` stays a plain
 * `Record<string, string>` (the persisted contract, §14.2) — no migration, and
 * an older reader still sees something sensible.
 *
 *   "#E23359"          plain colour
 *   "glitter:#E23359"  colour with a finish
 */
export function paintSpec(hex: string, finish: Finish): string {
  return finish === 'solid' ? hex : `${finish}:${hex}`;
}

export function parsePaint(spec: string): { finish: Finish; hex: string } {
  const at = spec.indexOf(':');
  if (at === -1) return { finish: 'solid', hex: spec };
  const finish = spec.slice(0, at) as Finish;
  const hex = spec.slice(at + 1);
  return FINISHES.some((f) => f.id === finish) ? { finish, hex } : { finish: 'solid', hex: spec };
}

/** The plain colour behind a spec — for swatches, previews and contrast. */
export function paintColor(spec: string): string {
  return parsePaint(spec).hex;
}

const patternCache = new Map<string, CanvasPattern | string>();

/**
 * Resolves a paint spec to something `fillStyle` accepts.
 *
 * Textured finishes are built once as a small tile and cached, so a region
 * with a thousand glitter flecks costs one `drawImage` per frame rather than a
 * thousand arcs — the canvas repaints on every pointer move.
 */
export function fillStyleFor(ctx: CanvasRenderingContext2D, spec: string): string | CanvasPattern {
  const { finish, hex } = parsePaint(spec);
  if (finish === 'solid') return hex;
  const key = `${finish}:${hex}`;
  const hit = patternCache.get(key);
  if (hit) return hit;
  const tile = buildTile(finish, hex);
  const pattern = ctx.createPattern(tile, 'repeat');
  const style = pattern ?? hex;
  patternCache.set(key, style);
  return style;
}

function buildTile(finish: Finish, hex: string): HTMLCanvasElement {
  const size = 64;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d')!;
  g.fillStyle = hex;
  g.fillRect(0, 0, size, size);
  const rand = rng(hashString(finish + hex));

  if (finish === 'glitter') {
    for (let i = 0; i < 90; i++) {
      const x = rand() * size;
      const y = rand() * size;
      const r = 0.5 + rand() * 1.4;
      g.fillStyle = rand() > 0.5 ? 'rgba(255,255,255,0.95)' : mix(hex, '#FFFFFF', 0.7);
      g.globalAlpha = 0.35 + rand() * 0.65;
      g.beginPath();
      g.arc(x, y, r, 0, Math.PI * 2);
      g.fill();
    }
    g.globalAlpha = 1;
  } else if (finish === 'waves') {
    g.strokeStyle = mix(hex, '#FFFFFF', 0.45);
    g.lineWidth = 7;
    g.lineCap = 'round';
    for (let row = -1; row < 5; row++) {
      const y = row * 16 + 8;
      g.beginPath();
      for (let x = 0; x <= size; x += 4) {
        const yy = y + Math.sin((x / size) * Math.PI * 2) * 5;
        if (x === 0) g.moveTo(x, yy);
        else g.lineTo(x, yy);
      }
      g.stroke();
    }
  } else if (finish === 'dots') {
    g.fillStyle = mix(hex, '#FFFFFF', 0.55);
    for (let ry = 0; ry < 4; ry++) {
      for (let rx = 0; rx < 4; rx++) {
        const x = rx * 16 + (ry % 2 ? 16 : 8);
        g.beginPath();
        g.arc(x % size, ry * 16 + 8, 4, 0, Math.PI * 2);
        g.fill();
      }
    }
  } else if (finish === 'stars') {
    g.fillStyle = mix(hex, '#FFFFFF', 0.7);
    for (let i = 0; i < 6; i++) {
      star(g, rand() * size, rand() * size, 4 + rand() * 3);
    }
  } else if (finish === 'stripes') {
    g.strokeStyle = mix(hex, '#000000', 0.18);
    g.lineWidth = 9;
    for (let i = -1; i < 6; i++) {
      g.beginPath();
      g.moveTo(i * 22 - 10, -10);
      g.lineTo(i * 22 + 64, size + 10);
      g.stroke();
    }
  }
  return c;
}

/** The product's four-point star, drawn on a canvas rather than in SVG. */
export function star(g: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  g.beginPath();
  g.moveTo(cx, cy - r);
  g.quadraticCurveTo(cx + r * 0.16, cy - r * 0.16, cx + r, cy);
  g.quadraticCurveTo(cx + r * 0.16, cy + r * 0.16, cx, cy + r);
  g.quadraticCurveTo(cx - r * 0.16, cy + r * 0.16, cx - r, cy);
  g.quadraticCurveTo(cx - r * 0.16, cy - r * 0.16, cx, cy - r);
  g.fill();
}
